import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import DOMPurify from 'dompurify';
import EmojiPicker from 'emoji-picker-react';
import TextareaAutosize from 'react-textarea-autosize';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from '../hooks/use-toast';
import TaskPreview from './TaskPreview';
import { parseTaskMentions, parseSlashCommand, getAvailableCommands, parseDateString } from '../utils/chatUtils';
import { 
  Send, Hash, Plus, Settings, Lock, Globe, Trash2, UserPlus, 
  Smile, Paperclip, AtSign, Search, Users, X, MessageCircle, ListTodo, MoreVertical
} from 'lucide-react';


// Memoized Message Component for performance
const MessageItem = memo(({ message, showAvatar, isCurrentUser, isDMMode, user, workspaceMembers, startDM, isUserOnline, getMessageTimestamp, onCreateTask, navigate, workspaceId }) => {
  const [showMenu, setShowMenu] = useState(false);
  
  const handleAvatarClick = useCallback(() => {
    if (message.user_id === user._id || message.sender_id === user._id) return;
    
    if (!isDMMode) {
      const senderId = message.user_id || message.sender_id;
      let member = (workspaceMembers || []).find(m => m._id === senderId);
      
      if (!member) {
        member = {
          _id: senderId,
          name: message.user_name || message.sender_name || user?.name || 'Unknown User',
          email: message.user_email || `user@unknown`,
          picture: message.user_avatar || message.sender_avatar || null
        };
      }
      
      startDM(member);
    }
  }, [message, user, isDMMode, workspaceMembers, startDM]);

  const userName = message.user_name || message.sender_name || user?.name || 'Unknown User';
  const userInitial = userName.charAt(0).toUpperCase();

  // Parse message content for task mentions
  const messageParts = useMemo(() => parseTaskMentions(message.content), [message.content]);
  
  const handleTaskNavigation = useCallback((workspace_id, boardId, taskId) => {
    if (navigate && workspace_id && boardId && taskId) {
      navigate(`/board?workspace=${workspace_id}&board=${boardId}&task=${taskId}`);
    }
  }, [navigate]);

  return (
    <div className={`group flex ${isCurrentUser ? 'justify-end' : 'justify-start'} ${showAvatar ? 'mt-4' : 'mt-1'}`}>
      <div className={`relative flex items-start max-w-2xl ${isCurrentUser ? 'flex-row-reverse' : 'flex-row'}`}>
        {showAvatar ? (
          <button
            onClick={handleAvatarClick}
            className={`w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-semibold flex-shrink-0 ${
              isCurrentUser ? 'ml-3' : 'mr-3'
            } ${
              !isCurrentUser && !isDMMode ? 'hover:ring-2 hover:ring-orange-300 cursor-pointer transition-all' : ''
            }`}
            title={!isCurrentUser && !isDMMode ? `Message ${userName}` : ''}
          >
            {userInitial}
          </button>
        ) : (
          <div className={`w-10 flex items-center justify-center ${isCurrentUser ? 'ml-3' : 'mr-3'}`}>
            <span className="text-xs text-gray-400 opacity-0 hover:opacity-100 transition-opacity">
              {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          {showAvatar && (
            <div className={`flex items-baseline mb-1 gap-2 ${isCurrentUser ? 'flex-row-reverse' : 'flex-row'}`}>
              <span className={`font-semibold ${isCurrentUser ? 'text-orange-600' : 'text-gray-900'}`}>
                {userName}
              </span>
              {!isCurrentUser && isUserOnline(message.user_id || message.sender_id) && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                  Online
                </span>
              )}
              <span className="text-xs text-gray-500">
                {getMessageTimestamp(message.createdAt)}
              </span>
            </div>
          )}
          
          {/* Message content with task mentions */}
          <div className="relative">
            <div className={`text-gray-800 break-words whitespace-pre-wrap text-sm px-4 py-2 rounded-lg ${
              isCurrentUser 
                ? 'bg-orange-100 text-orange-900 rounded-br-none' 
                : 'bg-gray-100 text-gray-900 rounded-bl-none'
            }`}>
              {messageParts.map((part, index) => 
                part.isTask ? (
                  <TaskPreview 
                    key={index}
                    taskId={part.taskId}
                    onNavigate={handleTaskNavigation}
                  />
                ) : (
                  <span key={index}>{part.text}</span>
                )
              )}
            </div>
            
            {/* Message menu button */}
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="absolute -right-2 top-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-white rounded border border-gray-200 hover:bg-gray-50"
            >
              <MoreVertical className="h-3 w-3 text-gray-500" />
            </button>
            
            {/* Context menu */}
            {showMenu && (
              <div className="absolute right-0 top-8 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[150px]">
                <button
                  onClick={() => {
                    onCreateTask(message);
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <ListTodo className="h-4 w-4" />
                  Create Task
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

MessageItem.displayName = 'MessageItem';

const ChatIntegrated = ({ workspaceId, userRole, workspaceMembers }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [channels, setChannels] = useState([]);
  const [currentChannel, setCurrentChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // DM State
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [isDMMode, setIsDMMode] = useState(false);
  
  // UI State
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [showChannelSettings, setShowChannelSettings] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showNewDMDialog, setShowNewDMDialog] = useState(false);
  const [dmSearchQuery, setDmSearchQuery] = useState('');
  
  // Task Integration State
  const [showCreateTaskDialog, setShowCreateTaskDialog] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskBoard, setTaskBoard] = useState('');
  const [boards, setBoards] = useState([]);
  
  // Slash Command State
  const [showCommandHelp, setShowCommandHelp] = useState(false);
  const [commandSuggestions, setCommandSuggestions] = useState([]);
  
  // Create Channel State
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelDescription, setNewChannelDescription] = useState('');
  const [newChannelPrivate, setNewChannelPrivate] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');
  
  // Online Status & Typing
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  
  const messagesEndRef = useRef(null);
  const commandHelpRef = useRef(null);
  const messageInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Click outside emoji picker to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  useEffect(() => {
    if (!workspaceId) return;
    
    fetchChannels();
    fetchConversations();
    
    // Initialize Socket.IO - use /api prefix to route through backend
    const newSocket = io(API_BASE_URL, {
      path: '/api/socket.io/',
      transports: ['polling', 'websocket'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });
    
    newSocket.on('connect', () => {
      console.log('✅ Socket connected:', newSocket.id);
      newSocket.emit('join_workspace', { workspace_id: workspaceId, user_id: user._id });
    });
    
    newSocket.on('new_message', (message) => {
      console.log('📨 New message received:', message);
      message.content = DOMPurify.sanitize(message.content);
      
      // Don't add if it's a duplicate (we already added it optimistically)
      setMessages(prev => {
        // Remove temp message if exists
        const withoutTemp = prev.filter(m => !m._id.startsWith('temp-'));
        // Add new message if not already present
        const exists = withoutTemp.some(m => m._id === message._id);
        if (exists) return withoutTemp;
        return [...withoutTemp, message];
      });
    });
    
    newSocket.on('new_dm', (dm) => {
      console.log('💬 New DM received:', dm);
      dm.content = DOMPurify.sanitize(dm.content);
      if (isDMMode && currentConversation?._id === dm.conversation_id) {
        setMessages(prev => {
          // Remove temp message if exists
          const withoutTemp = prev.filter(m => !m._id.startsWith('temp-'));
          // Add new DM if not already present
          const exists = withoutTemp.some(m => m._id === dm._id);
          if (exists) return withoutTemp;
          return [...withoutTemp, dm];
        });
      }
    });
    
    newSocket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error);
    });
    
    newSocket.on('error', (error) => {
      console.error('❌ Socket error:', error);
    });
    
    newSocket.on('user_online', (data) => {
      setOnlineUsers(prev => [...new Set([...prev, data.user_id])]);
    });
    
    newSocket.on('user_offline', (data) => {
      setOnlineUsers(prev => prev.filter(id => id !== data.user_id));
    });
    
    newSocket.on('user_typing', (data) => {
      if (data.user_id !== user._id) {
        setTypingUsers(prev => [...new Set([...prev, data.user_name])]);
        setTimeout(() => {
          setTypingUsers(prev => prev.filter(name => name !== data.user_name));
        }, 3000);
      }
    });
    
    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
    });
    
    setSocket(newSocket);
    
    return () => {
      if (newSocket) {
        newSocket.emit('leave_workspace', { workspace_id: workspaceId });
        newSocket.disconnect();
      }
    };
  }, [workspaceId, user._id]);
  

  // Fetch boards for task creation
  useEffect(() => {
    const fetchBoards = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/boards?workspace_id=${workspaceId}`, { withCredentials: true });
        setBoards(response.data);
      } catch (error) {
        console.error('Error fetching boards:', error);
      }
    };
    
    if (workspaceId) {
      fetchBoards();
    }
  }, [workspaceId]);
  
  // Handle create task from message
  const handleCreateTaskFromMessage = useCallback((message) => {
    setSelectedMessage(message);
    setTaskTitle(''); // Let user define the title
    setShowCreateTaskDialog(true);
  }, []);
  
  // Create task from chat
  const createTaskFromChat = async () => {
    if (!taskTitle.trim() || !taskBoard) {
      toast({ title: 'Please provide task title and select a board', variant: 'destructive' });
      return;
    }
    
    try {
      const taskData = {
        boardId: taskBoard,
        task: taskTitle,
        message_id: selectedMessage?._id,
        channel_id: currentChannel?._id || null,
        conversation_id: currentConversation?._id || null,
        message_content: selectedMessage?.content,
        assignees: [],
        notes: `Created from chat message by ${user.name}`
      };
      
      const response = await axios.post(
        `${API_BASE_URL}/api/tasks/from-chat?workspace_id=${workspaceId}`,
        taskData,
        { withCredentials: true }
      );
      
      toast({ 
        title: '✅ Task created successfully!',
        description: `"${taskTitle}" has been added to your board`
      });
      
      // Reset and close dialog
      setShowCreateTaskDialog(false);
      setTaskTitle('');
      setTaskBoard('');
      setSelectedMessage(null);
      
      // Navigate to task
      if (response.data.boardId && response.data._id) {
        navigate(`/board?workspace=${workspaceId}&board=${response.data.boardId}&task=${response.data._id}`);
      }
    } catch (error) {
      console.error('Error creating task:', error);
      toast({ 
        title: 'Error creating task',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive'
      });
    }
  };
  

  const fetchChannels = async () => {
    try {
      setLoading(true);
      const channelsRes = await axios.get(`${API_BASE_URL}/api/channels?workspace_id=${workspaceId}`, { withCredentials: true });
      setChannels(channelsRes.data);
      
      // Select first channel by default
      if (channelsRes.data.length > 0 && !currentChannel && !isDMMode) {
        selectChannel(channelsRes.data[0]);
      }
    } catch (error) {
      console.error('Error fetching channels:', error);
      toast({ title: 'Error loading channels', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  
  const fetchConversations = async () => {
    try {
      const convRes = await axios.get(`${API_BASE_URL}/api/conversations?workspace_id=${workspaceId}`, { withCredentials: true });
      
      // Filter out invalid conversations immediately
      const validConversations = convRes.data.filter(conv => {
        // Must have other_user with _id and name
        return conv?.other_user?._id && conv?.other_user?.name;
      });
      
      console.log('Valid conversations:', validConversations.length, 'out of', convRes.data.length);
      setConversations(validConversations);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setConversations([]); // Set empty array on error
    }
  };
  
  const startDM = useCallback(async (member) => {
    try {
      // Validate member object
      if (!member || !member._id || !member.name) {
        console.error('Invalid member object:', member);
        toast({ 
          title: 'Error starting conversation', 
          description: 'User information is incomplete',
          variant: 'destructive' 
        });
        return;
      }
      
      // Get or create conversation
      const response = await axios.post(
        `${API_BASE_URL}/api/conversations`,
        {
          workspace_id: workspaceId,
          other_user_id: member._id
        },
        { withCredentials: true }
      );
      
      const conversation = response.data.conversation;
      
      // Ensure other_user is properly set
      if (!conversation.other_user || !conversation.other_user._id) {
        conversation.other_user = member;
      }
      
      console.log('Starting DM with conversation:', conversation);
      
      // Switch to DM mode
      setIsDMMode(true);
      setCurrentChannel(null);
      setCurrentConversation(conversation);
      
      // Leave channel if in one
      if (currentChannel && socket) {
        socket.emit('leave_channel', { channel_id: currentChannel._id });
      }
      
      // Join conversation room
      if (socket) {
        socket.emit('join_conversation', { conversation_id: conversation._id });
      }
      
      // Fetch DM messages
      const messagesRes = await axios.get(
        `${API_BASE_URL}/api/conversations/${conversation._id}/messages`,
        { withCredentials: true }
      );
      
      const sanitizedMessages = messagesRes.data.map(msg => ({
        ...msg,
        content: DOMPurify.sanitize(msg.content)
      }));
      
      setMessages(sanitizedMessages);
      
      // Update conversations list
      fetchConversations();
      
      setTimeout(() => messageInputRef.current?.focus(), 100);
    } catch (error) {
      console.error('Error starting DM:', error);
      toast({ title: 'Error starting conversation', variant: 'destructive' });
    }
  }, [workspaceId, user]);
  
  const selectChannel = useCallback(async (channel) => {
    try {
      // Switch to channel mode
      setIsDMMode(false);
      setCurrentConversation(null);
      setCurrentChannel(channel);
      
      // Leave previous channel or conversation
      if (currentChannel && socket) {
        socket.emit('leave_channel', { channel_id: currentChannel._id });
      }
      if (currentConversation && socket) {
        socket.emit('leave_conversation', { conversation_id: currentConversation._id });
      }
      
      // Join new channel
      if (socket) {
        console.log('🔗 Joining channel:', channel.name || 'unnamed', channel._id);
        socket.emit('join_channel', { 
          channel_id: channel._id,
          workspace_id: workspaceId
        });
      }
      
      // Fetch messages
      const messagesRes = await axios.get(
        `${API_BASE_URL}/api/channels/${channel._id}/messages`,
        { withCredentials: true }
      );
      
      const sanitizedMessages = messagesRes.data.map(msg => ({
        ...msg,
        content: DOMPurify.sanitize(msg.content)
      }));
      
      setMessages(sanitizedMessages);
      
      setTimeout(() => messageInputRef.current?.focus(), 100);
    } catch (error) {
      console.error('Error selecting channel:', error);
      toast({ title: 'Error loading messages', variant: 'destructive' });
    }
  }, [socket, workspaceId]);
  

  // Handle slash commands
  const handleSlashCommand = useCallback(async (commandData) => {
    const { command, error, taskId, title, assignee, status, date, priority, comment, showHelp } = commandData;
    
    if (error) {
      toast({ title: error, variant: 'destructive' });
      return false;
    }
    
    if (showHelp) {
      setShowCommandHelp(true);
      return true;
    }
    
    try {
      switch(command) {
        case 'create-task':
        case 'task':
          if (!boards.length) {
            toast({ title: 'No boards available', description: 'Create a board first', variant: 'destructive' });
            return false;
          }
          // Use first board as default or open dialog
          setTaskTitle(title);
          setTaskBoard(boards[0]._id);
          setShowCreateTaskDialog(true);
          return true;
        
        case 'assign':
          // Find user by name
          const member = (workspaceMembers || []).find(m => 
            m.name.toLowerCase().includes(assignee.toLowerCase())
          );
          
          if (!member) {
            toast({ title: `User @${assignee} not found`, variant: 'destructive' });
            return false;
          }
          
          await axios.patch(
            `${API_BASE_URL}/api/tasks/${taskId}/quick-update`,
            { assignees: [{ name: member.name, avatar: member.name.charAt(0), color: '#1C3E53' }] },
            { withCredentials: true }
          );
          
          toast({ title: `✅ Task assigned to ${member.name}` });
          return true;
        
        case 'status':
        case 'done':
        case 'complete':
          const statusMap = {
            'todo': { value: 'todo', label: 'To Do', color: '#94A3B8' },
            'in-progress': { value: 'in-progress', label: 'In Progress', color: '#3B82F6' },
            'done': { value: 'done', label: 'Done', color: '#10B981' }
          };
          
          const statusObj = statusMap[status] || statusMap['done'];
          
          await axios.patch(
            `${API_BASE_URL}/api/tasks/${taskId}/quick-update`,
            { status: statusObj },
            { withCredentials: true }
          );
          
          toast({ title: `✅ Task status updated to ${statusObj.label}` });
          return true;
        
        case 'due':
        case 'deadline':
          const parsedDate = parseDateString(date);
          
          await axios.patch(
            `${API_BASE_URL}/api/tasks/${taskId}/quick-update`,
            { date: parsedDate },
            { withCredentials: true }
          );
          
          toast({ title: `✅ Task deadline set to ${parsedDate}` });
          return true;
        
        case 'priority':
          const priorityMap = {
            'urgent': { value: 'urgent', label: 'Urgent', color: '#EF4444' },
            'high': { value: 'high', label: 'High', color: '#F59E0B' },
            'medium': { value: 'medium', label: 'Medium', color: '#3B82F6' },
            'low': { value: 'low', label: 'Low', color: '#10B981' }
          };
          
          const priorityObj = priorityMap[priority];
          if (!priorityObj) {
            toast({ title: 'Invalid priority', description: 'Use: urgent, high, medium, or low', variant: 'destructive' });
            return false;
          }
          
          // Priority changes need full task update
          const task = await axios.get(`${API_BASE_URL}/api/tasks/${taskId}`, { withCredentials: true });
          await axios.put(
            `${API_BASE_URL}/api/tasks/${taskId}`,
            { ...task.data, priority: priorityObj },
            { withCredentials: true }
          );
          
          toast({ title: `✅ Task priority updated to ${priorityObj.label}` });
          return true;
        
        case 'comment':
          await axios.post(
            `${API_BASE_URL}/api/tasks/${taskId}/comments`,
            { content: comment },
            { withCredentials: true }
          );
          
          toast({ title: `✅ Comment added to task` });
          return true;
        
        default:
          toast({ title: `Unknown command: /${command}`, variant: 'destructive' });
          return false;
      }
    } catch (error) {
      console.error('Error executing command:', error);
      toast({ 
        title: 'Command failed', 
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive' 
      });
      return false;
    }
  }, [boards, workspaceMembers]);
  

  const sendMessage = useCallback(async () => {
    if (!newMessage.trim() || !socket) {
      console.log('⚠️ Cannot send message:', { 
        hasMessage: !!newMessage.trim(), 
        hasSocket: !!socket,
        socketConnected: socket?.connected,
        isDMMode,
        hasChannel: !!currentChannel,
        hasConversation: !!currentConversation
      });
      return;
    }
    
    // Check if it's a slash command
    const commandData = parseSlashCommand(newMessage);
    if (commandData.isCommand) {
      const success = await handleSlashCommand(commandData);
      if (success) {
        setNewMessage(''); // Clear input after successful command
        setShowCommandHelp(false);
      }
      return;
    }
    
    const sanitizedMessage = DOMPurify.sanitize(newMessage.trim());
    
    if (isDMMode && currentConversation) {
      // Send DM
      console.log('📤 Sending DM:', { 
        conversation: currentConversation._id, 
        content: sanitizedMessage,
        socketConnected: socket.connected 
      });
      
      // Optimistic UI: Add message immediately
      const optimisticMessage = {
        _id: `temp-${Date.now()}`,
        conversation_id: currentConversation._id,
        sender_id: user._id,
        sender_name: user.name || 'Me',
        sender_avatar: user.avatar || null,
        content: sanitizedMessage,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setMessages(prev => [...prev, optimisticMessage]);
      
      socket.emit('send_dm', {
        conversation_id: currentConversation._id,
        workspace_id: workspaceId,
        sender_id: user._id,
        sender_name: user.name || 'Me',
        sender_avatar: user.avatar || null,
        content: sanitizedMessage
      });
    } else if (!isDMMode && currentChannel) {
      // Send channel message
      console.log('📤 Sending message:', { 
        channel: currentChannel.name || 'unnamed', 
        content: sanitizedMessage,
        socketConnected: socket.connected 
      });
      
      // Optimistic UI: Add message immediately
      const optimisticMessage = {
        _id: `temp-${Date.now()}`,
        channel_id: currentChannel._id,
        user_id: user._id,
        user_name: user.name || 'Me',
        user_avatar: user.avatar || null,
        content: sanitizedMessage,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setMessages(prev => [...prev, optimisticMessage]);
      
      socket.emit('send_message', {
        channel_id: currentChannel._id,
        workspace_id: workspaceId,
        user_id: user._id,
        user_name: user.name || 'Me',
        user_avatar: user.avatar || null,
        content: sanitizedMessage
      });
    }
    
    setNewMessage('');
    setShowEmojiPicker(false);
  }, [newMessage, socket, currentChannel, currentConversation, isDMMode, user, workspaceId, handleSlashCommand]);
  
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);
  
  const handleTyping = () => {
    if (socket && currentChannel) {
      socket.emit('typing', {
        channel_id: currentChannel._id,
        user_id: user._id,
        user_name: user.name
      });
    }
  };
  
  const handleMessageChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    handleTyping();
    
    // Check for slash commands
    if (value.startsWith('/')) {
      const commands = getAvailableCommands();
      const query = value.substring(1).toLowerCase();
      const filtered = commands.filter(cmd => 
        cmd.command.substring(1).startsWith(query)
      );
      setCommandSuggestions(filtered);
    } else {
      setCommandSuggestions([]);
    }
    
    // Check for @ mentions
    const lastWord = value.split(' ').pop();
    if (lastWord.startsWith('@')) {
      setMentionSearch(lastWord.substring(1));
      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
  };
  
  const insertMention = (member) => {
    if (!member || !member.name) {
      console.warn('Cannot insert mention for member without name:', member);
      return;
    }
    const words = newMessage.split(' ');
    words[words.length - 1] = `@${member.name} `;
    setNewMessage(words.join(' '));
    setShowMentions(false);
    messageInputRef.current?.focus();
  };
  
  const selectCommandSuggestion = (command) => {
    setNewMessage(command.command + ' ');
    setCommandSuggestions([]);
    messageInputRef.current?.focus();
  };
  
  const onEmojiClick = (emojiData) => {
    setNewMessage(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
    messageInputRef.current?.focus();
  };
  
  const createChannel = async () => {
    if (!newChannelName.trim()) return;
    
    try {
      await axios.post(
        `${API_BASE_URL}/api/channels?workspace_id=${workspaceId}`,
        {
          name: newChannelName.trim().toLowerCase(),
          description: newChannelDescription,
          is_private: newChannelPrivate,
          members: newChannelPrivate ? selectedMembers : []
        },
        { withCredentials: true }
      );
      
      toast({ title: `Channel #${newChannelName} created!` });
      setNewChannelName('');
      setNewChannelDescription('');
      setNewChannelPrivate(false);
      setSelectedMembers([]);
      setShowCreateChannel(false);
      fetchChannels();
    } catch (error) {
      toast({ 
        title: 'Error creating channel',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive'
      });
    }
  };
  
  const openChannelSettings = (channel) => {
    setSelectedChannel(channel);
    setShowChannelSettings(true);
  };
  
  const deleteChannel = async () => {
    if (!selectedChannel || !window.confirm(`Delete #${selectedChannel.name || 'this channel'}? All messages will be lost.`)) return;
    
    try {
      await axios.delete(
        `${API_BASE_URL}/api/channels/${selectedChannel._id}`,
        { withCredentials: true }
      );
      
      toast({ title: `Channel #${selectedChannel.name || 'Channel'} deleted` });
      setShowChannelSettings(false);
      setSelectedChannel(null);
      setCurrentChannel(null);
      fetchChannels();
    } catch (error) {
      toast({
        title: 'Error deleting channel',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive'
      });
    }
  };
  
  const toggleMemberSelection = (memberId) => {
    setSelectedMembers(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };
  
  const canManageChannel = () => {
    return userRole === 'owner' || userRole === 'admin';
  };
  
  const isUserOnline = (userId) => {
    return onlineUsers.includes(userId);
  };
  
  const getMessageTimestamp = (createdAt) => {
    const date = new Date(createdAt);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };
  
  const filteredChannels = useMemo(() => 
    channels.filter(ch => 
      ch.name && ch.name.toLowerCase().includes(searchQuery.toLowerCase())
    ), [channels, searchQuery]
  );
  
  const filteredMembers = useMemo(() => 
    (workspaceMembers || []).filter(m => 
      m.name && m.name.toLowerCase().includes(mentionSearch.toLowerCase())
    ), [workspaceMembers, mentionSearch]
  );
  
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading chat...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-full flex">
      {/* LEFT PANEL - Channels & DMs */}
      <div className="w-72 bg-white border-r flex flex-col">
        {/* Search */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search channels..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 text-sm border-gray-200"
            />
          </div>
        </div>
        
        {/* Channels List */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-gray-500 uppercase">Channels</span>
            {canManageChannel() && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 hover:bg-orange-100 hover:text-orange-600"
                onClick={() => setShowCreateChannel(true)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>
          
          <div className="space-y-1">
            {filteredChannels.map(channel => (
              <button
                key={channel._id}
                onClick={() => selectChannel(channel)}
                className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between group transition-colors ${
                  currentChannel?._id === channel._id 
                    ? 'bg-orange-50 text-orange-700 border border-orange-200' 
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center flex-1 min-w-0">
                  {channel.is_private ? (
                    <Lock className="h-4 w-4 mr-2 flex-shrink-0 text-gray-500" />
                  ) : (
                    <Hash className="h-4 w-4 mr-2 flex-shrink-0 text-gray-500" />
                  )}
                  <span className="truncate text-sm font-medium">{channel.name || 'Unnamed Channel'}</span>
                </div>
                
                {canManageChannel() && channel.name && channel.name !== 'general' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      openChannelSettings(channel);
                    }}
                  >
                    <Settings className="h-3 w-3 text-gray-500" />
                  </Button>
                )}
              </button>
            ))}
          </div>
          
          {/* Direct Messages Section */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500 uppercase">Direct Messages</span>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 hover:bg-orange-100 hover:text-orange-600 rounded-full"
                onClick={() => setShowNewDMDialog(true)}
                title="New Message"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="space-y-1">
              {/* Recent Conversations Only */}
              {conversations.length > 0 ? (
                <>
                  {conversations.map(conv => {
                      try {
                        // ULTRA-STRICT: Vérifier CHAQUE niveau
                        if (!conv) {
                          console.warn('Conversation is null/undefined');
                          return null;
                        }
                        
                        if (!conv._id) {
                          console.warn('Conversation missing _id');
                          return null;
                        }
                        
                        if (!conv.other_user) {
                          console.warn('Conversation missing other_user:', conv._id);
                          return null;
                        }
                        
                        if (!conv.other_user._id) {
                          console.warn('other_user missing _id:', conv._id);
                          return null;
                        }
                        
                        // Don't filter out conversations with missing names, use default instead
                        // if (!conv.other_user.name) {
                        //   console.warn('other_user missing name:', conv._id);
                        //   return null;
                        // }
                        
                        // SEULEMENT MAINTENANT créer l'objet avec optional chaining en plus
                        const otherUser = {
                          _id: conv.other_user?._id || 'unknown',
                          name: conv.other_user?.name || 'Unknown User',
                          email: conv.other_user?.email || '',
                          picture: conv.other_user?.picture || null
                        };
                        
                        // Double-check que otherUser a bien un _id valide
                        if (!otherUser._id || otherUser._id === 'unknown') {
                          console.warn('Failed to create valid otherUser - missing _id');
                          return null;
                        }
                        
                        return (
                          <button
                            key={conv._id}
                            onClick={(e) => {
                              try {
                                e.preventDefault();
                                e.stopPropagation();
                                
                                // Try to find in workspace members first
                                let member = (workspaceMembers || []).find(m => m._id === otherUser._id);
                                
                                // If not found, use other_user data directly
                                if (!member) {
                                  member = otherUser;
                                }
                                
                                console.log('Starting DM with member:', member);
                                startDM(member);
                              } catch (err) {
                                console.error('Error starting DM:', err);
                                toast({ title: 'Error opening conversation', variant: 'destructive' });
                              }
                            }}
                            className={`w-full text-left px-3 py-2 rounded-lg flex items-center transition-colors ${
                              isDMMode && currentConversation?._id === conv._id
                                ? 'bg-orange-50 border border-orange-200'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="relative">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-semibold text-sm">
                                {String(otherUser.name || 'U').charAt(0).toUpperCase()}
                              </div>
                              <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white ${
                                isUserOnline(otherUser._id) ? 'bg-green-500' : 'bg-gray-400'
                              }`}></div>
                            </div>
                            <div className="ml-3 flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-700 truncate">{otherUser.name || 'Unknown User'}</p>
                              {conv.last_message && (
                                <p className="text-xs text-gray-500 truncate">{conv.last_message}</p>
                              )}
                            </div>
                          </button>
                        );
                      } catch (error) {
                        console.error('Error rendering conversation:', conv?._id, error);
                        return null;
                      }
                    }).filter(Boolean)}
                </>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <MessageCircle className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No conversations yet</p>
                  <p className="text-xs mt-1">Click + to start messaging</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* CENTER PANEL - Messages */}
      <div className="flex-1 flex flex-col bg-gray-50">
        {/* Channel/DM Header */}
        {(currentChannel || currentConversation) && (
          <div className="h-16 border-b bg-white flex items-center px-6 justify-between">
            <div className="flex items-center">
              {isDMMode && currentConversation ? (
                <>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-semibold mr-3">
                    {currentConversation.other_user?.name ? currentConversation.other_user.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">{currentConversation.other_user?.name || 'Unknown User'}</h2>
                    <p className="text-xs text-gray-500">Direct Message</p>
                  </div>
                </>
              ) : currentChannel ? (
                <>
                  {currentChannel.is_private ? (
                    <Lock className="h-5 w-5 text-gray-500 mr-3" />
                  ) : (
                    <Hash className="h-5 w-5 text-gray-500 mr-3" />
                  )}
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">{currentChannel.name || 'Channel'}</h2>
                    {currentChannel.description && (
                      <p className="text-xs text-gray-500">{currentChannel.description}</p>
                    )}
                  </div>
                </>
              ) : null}
            </div>
            
            <div className="flex items-center space-x-2">
              {!isDMMode && (
                <>
                  <Button size="sm" variant="outline" className="border-gray-200">
                    <Users className="h-4 w-4 mr-2" />
                    {(workspaceMembers || []).length}
                  </Button>
                  {canManageChannel() && currentChannel?.name !== 'general' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openChannelSettings(currentChannel)}
                      className="hover:bg-orange-50 hover:text-orange-600"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {!currentChannel && !currentConversation ? (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <MessageCircle className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg">Select a channel to start chatting</p>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <MessageCircle className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg mb-2">No messages yet in #{currentChannel?.name || 'this channel'}</p>
                <p className="text-sm text-gray-400">Be the first to say something!</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 max-w-4xl mx-auto">
              {messages.map((message, index) => {
                const showAvatar = index === 0 || messages[index - 1].user_id !== message.user_id || messages[index - 1].sender_id !== message.sender_id;
                const isCurrentUser = message.user_id === user?._id || message.sender_id === user?._id;
                
                return (
                  <MessageItem
                    key={message._id}
                    message={message}
                    showAvatar={showAvatar}
                    isCurrentUser={isCurrentUser}
                    isDMMode={isDMMode}
                    user={user}
                    workspaceMembers={workspaceMembers}
                    startDM={startDM}
                    isUserOnline={isUserOnline}
                    getMessageTimestamp={getMessageTimestamp}
                    onCreateTask={handleCreateTaskFromMessage}
                    navigate={navigate}
                    workspaceId={workspaceId}
                  />
                );
              })}
              
              {/* Typing Indicator */}
              {typingUsers.length > 0 && (
                <div className="flex items-center text-sm text-gray-500 italic">
                  <div className="flex space-x-1 mr-2">
                    <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  {typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
        
        {/* Input Bar */}
        {(currentChannel || currentConversation) && (
          <div className="border-t bg-white p-4">
            <div className="max-w-4xl relative">
              {/* Mentions Autocomplete */}
              {showMentions && (
                <div className="mb-2 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredMembers.map(member => (
                    <button
                      key={member._id}
                      onClick={() => insertMention(member)}
                      className="w-full text-left px-4 py-2 hover:bg-orange-50 flex items-center"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-semibold text-sm mr-3">
                        {(member.name || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{member.name || 'Unknown'}</div>
                        <div className="text-xs text-gray-500">{member.email}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Command Suggestions */}
              {commandSuggestions.length > 0 && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {commandSuggestions.map((cmd, index) => (
                    <button
                      key={index}
                      onClick={() => selectCommandSuggestion(cmd)}
                      className="w-full text-left px-4 py-3 hover:bg-orange-50 border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <div className="flex items-start gap-2">
                        <code className="text-orange-600 font-mono text-sm font-semibold">
                          {cmd.command} {cmd.args}
                        </code>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{cmd.description}</p>
                    </button>
                  ))}
                </div>
              )}
              
              <div className="flex items-end gap-2 bg-gray-50 rounded-lg border border-gray-200 p-2">
                {/* Emoji Picker Button */}
                <div className="relative" ref={emojiPickerRef}>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="text-gray-500 hover:text-orange-500 hover:bg-orange-50"
                  >
                    <Smile className="h-5 w-5" />
                  </Button>
                  
                  {showEmojiPicker && (
                    <div className="absolute bottom-12 left-0 z-50">
                      <EmojiPicker onEmojiClick={onEmojiClick} />
                    </div>
                  )}
                </div>
                
                {/* File Upload Button */}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-gray-500 hover:text-orange-500 hover:bg-orange-50"
                >
                  <Paperclip className="h-5 w-5" />
                </Button>
                
                {/* Text Input */}
                <TextareaAutosize
                  ref={messageInputRef}
                  placeholder={isDMMode && currentConversation 
                    ? `Message ${currentConversation.other_user?.name || 'User'}` 
                    : currentChannel 
                    ? `Message #${currentChannel.name || 'channel'}` 
                    : 'Select a channel or DM'}
                  value={newMessage}
                  onChange={handleMessageChange}
                  onKeyPress={handleKeyPress}
                  minRows={1}
                  maxRows={6}
                  className="flex-1 resize-none bg-transparent border-none focus:outline-none text-sm py-2 px-2"
                  disabled={!socket}
                />
                
                {/* Send Button */}
                <Button 
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || !socket}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  size="sm"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="mt-2 text-xs text-gray-500 flex items-center">
                <AtSign className="h-3 w-3 mr-1" />
                Use @ to mention someone · Shift + Enter for new line
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Create Channel Dialog */}
      <Dialog open={showCreateChannel} onOpenChange={setShowCreateChannel}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Channel</DialogTitle>
            <DialogDescription>
              Add a new channel to organize conversations
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Channel Name</label>
              <Input
                placeholder="e.g., marketing, development"
                value={newChannelName}
                onChange={(e) => setNewChannelName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !newChannelPrivate && createChannel()}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Description (optional)</label>
              <Input
                placeholder="What's this channel about?"
                value={newChannelDescription}
                onChange={(e) => setNewChannelDescription(e.target.value)}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Privacy</label>
              <Select value={newChannelPrivate ? 'private' : 'public'} onValueChange={(val) => {
                setNewChannelPrivate(val === 'private');
                if (val === 'public') setSelectedMembers([]);
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">
                    <div className="flex items-center">
                      <Globe className="h-4 w-4 mr-2" />
                      Public - Everyone can access
                    </div>
                  </SelectItem>
                  <SelectItem value="private">
                    <div className="flex items-center">
                      <Lock className="h-4 w-4 mr-2" />
                      Private - Invite only
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {newChannelPrivate && (
              <div>
                <label className="text-sm font-medium mb-2 block">Select Members</label>
                <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {(workspaceMembers || []).map(member => (
                    <div key={member._id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                      <Checkbox
                        checked={selectedMembers.includes(member._id)}
                        onCheckedChange={() => toggleMemberSelection(member._id)}
                        className="mr-3"
                      />
                      <span className="text-sm">{member.name || 'Unknown'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateChannel(false);
              setNewChannelName('');
              setNewChannelDescription('');
              setNewChannelPrivate(false);
              setSelectedMembers([]);
            }}>Cancel</Button>
            <Button onClick={createChannel} className="bg-orange-500 hover:bg-orange-600" disabled={!newChannelName.trim()}>
              Create Channel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Channel Settings Dialog */}
      <Dialog open={showChannelSettings} onOpenChange={setShowChannelSettings}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Channel Settings</DialogTitle>
            <DialogDescription>
              Manage #{selectedChannel?.name || 'this channel'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                {selectedChannel?.is_private ? <Lock className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
                <div>
                  <p className="font-medium text-sm">
                    {selectedChannel?.is_private ? 'Private Channel' : 'Public Channel'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {selectedChannel?.is_private ? 'Only invited members can access' : 'All workspace members can access'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="pt-4 border-t">
              <Button
                variant="destructive"
                className="w-full"
                onClick={deleteChannel}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Channel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* New Direct Message Dialog */}
      <Dialog open={showNewDMDialog} onOpenChange={setShowNewDMDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-orange-500" />
              New Message
            </DialogTitle>
            <DialogDescription>
              Start a direct conversation with a team member
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search members..."
                  value={dmSearchQuery}
                  onChange={(e) => setDmSearchQuery(e.target.value)}
                  className="pl-9"
                  autoFocus
                />
              </div>
            </div>
            
            {/* Members List */}
            <div className="max-h-96 overflow-y-auto space-y-1">
              {(workspaceMembers || [])
                .filter(m => m._id !== user._id)
                .filter(m => 
                  (m.name && m.name.toLowerCase().includes(dmSearchQuery.toLowerCase())) ||
                  (m.email && m.email.toLowerCase().includes(dmSearchQuery.toLowerCase()))
                )
                .map(member => (
                  <button
                    key={member._id}
                    onClick={() => {
                      startDM(member);
                      setShowNewDMDialog(false);
                      setDmSearchQuery('');
                    }}
                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-orange-50 transition-colors flex items-center gap-3 group"
                  >
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-semibold">
                        {(member.name || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white ${
                        isUserOnline(member._id) ? 'bg-green-500' : 'bg-gray-400'
                      }`}></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900 truncate">{member.name || 'Unknown User'}</p>
                        {isUserOnline(member._id) && (
                          <span className="text-xs text-green-600 font-medium">Online</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">{member.email}</p>
                    </div>
                    <MessageCircle className="h-5 w-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
                  </button>
                ))}
              
              {(workspaceMembers || []).filter(m => m._id !== user._id).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">No other members in this workspace</p>
                </div>
              )}
              
              {dmSearchQuery && (workspaceMembers || [])
                .filter(m => m._id !== user._id)
                .filter(m => 
                  (m.name && m.name.toLowerCase().includes(dmSearchQuery.toLowerCase())) ||
                  (m.email && m.email.toLowerCase().includes(dmSearchQuery.toLowerCase()))
                ).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Search className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">No members found for "{dmSearchQuery}"</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Create Task from Message Dialog */}
      <Dialog open={showCreateTaskDialog} onOpenChange={setShowCreateTaskDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListTodo className="h-5 w-5 text-orange-500" />
              Create Task from Message
            </DialogTitle>
            <DialogDescription>
              Convert this conversation into an actionable task
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Original Message Preview */}
            {selectedMessage && (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Original message:</p>
                <p className="text-sm text-gray-700 line-clamp-3">{selectedMessage.content}</p>
              </div>
            )}
            
            {/* Task Title */}
            <div>
              <Label htmlFor="task-title">Task Title *</Label>
              <Input
                id="task-title"
                placeholder="e.g., Review design mockups"
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
                className="mt-1"
                autoFocus
              />
            </div>
            
            {/* Board Selection */}
            <div>
              <Label htmlFor="task-board">Board *</Label>
              <Select value={taskBoard} onValueChange={setTaskBoard}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a board" />
                </SelectTrigger>
                <SelectContent>
                  {boards.map(board => (
                    <SelectItem key={board._id} value={board._id}>
                      {board.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="text-xs text-gray-500 pt-2">
              💡 The task will be linked to this message, so you can always find the original discussion.
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCreateTaskDialog(false);
                setTaskTitle('');
                setTaskBoard('');
                setSelectedMessage(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={createTaskFromChat}
              disabled={!taskTitle.trim() || !taskBoard}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Command Help Dialog */}
      <Dialog open={showCommandHelp} onOpenChange={setShowCommandHelp}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              ⚡ Slash Commands
            </DialogTitle>
            <DialogDescription>
              Speed up your workflow with these powerful commands
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 py-4 max-h-96 overflow-y-auto">
            {getAvailableCommands().map((cmd, index) => (
              <div key={index} className="p-3 rounded-lg border border-gray-200 hover:border-orange-300 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <code className="text-orange-600 font-mono text-sm font-semibold">
                      {cmd.command} {cmd.args}
                    </code>
                    <p className="text-sm text-gray-600 mt-1">{cmd.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="border-t pt-4">
            <p className="text-xs text-gray-500">
              💡 <strong>Tips:</strong> Start typing "/" in the message box to see command suggestions. 
              You can mention tasks with #TASK-123 format.
            </p>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowCommandHelp(false)}>
              Got it!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatIntegrated;
